import React, { useState, useCallback } from 'react';
import { AlertCircle } from 'lucide-react';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import php from 'react-syntax-highlighter/dist/esm/languages/hljs/php';
import json from 'react-syntax-highlighter/dist/esm/languages/hljs/json';
import { githubGist } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { useMetadata } from '../utils/metadata';
import { CopyButton } from '../components/common/CopyButton';
import { DownloadButton } from '../components/common/DownloadButton';

SyntaxHighlighter.registerLanguage('php', php);
SyntaxHighlighter.registerLanguage('json', json);

function PhpConverter() {
  useMetadata({
    title: 'JSON to PHP Array Converter',
    description: 'Free online tool to convert JSON to PHP arrays. Convert JSON objects and arrays to PHP array syntax with proper formatting. Perfect for PHP developers working with JSON data.',
    keywords: ['json to php', 'php array converter', 'json converter', 'php tools', 'array syntax', 'php development', 'json to array']
  });
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');

  const convertToPhp = useCallback((jsonStr: string) => {
    try {
      if (!jsonStr.trim()) {
        setOutput('');
        setError('');
        return;
      }

      const data = JSON.parse(jsonStr);
      
      const convertValue = (value: any, indent: number = 1): string => {
        const spaces = '    '.repeat(indent);
        
        if (Array.isArray(value)) {
          if (value.length === 0) return '[]';
          
          const items = value.map(item => `${spaces}${convertValue(item, indent + 1)}`).join(",\n");
          return `[\n${items}\n${'    '.repeat(indent - 1)}]`;
        }
        
        if (value === null) return 'null';
        
        if (typeof value === 'object') {
          if (Object.keys(value).length === 0) return '[]';
          
          const items = Object.entries(value).map(([key, val]) => 
            `${spaces}'${key}' => ${convertValue(val, indent + 1)}`
          ).join(",\n");
          return `[\n${items}\n${'    '.repeat(indent - 1)}]`;
        }
        
        if (typeof value === 'string') return `'${value.replace(/'/g, "\\'")}'`;
        return String(value);
      };

      const phpArray = convertValue(data);
      setOutput(`<?php\n\n$array = ${phpArray};\n`);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid JSON input');
      setOutput('');
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInput(value);
    convertToPhp(value);
  };

  return (
    <div className="max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-4">JSON to PHP Array Converter</h1>
      <p className="text-gray-600 mb-8">
        Convert JSON objects and arrays to PHP array syntax. Paste your JSON in the input box below
        and see the PHP array representation instantly.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-medium text-gray-900">Input (JSON)</h2>
              <div className="flex space-x-2">
                <CopyButton text={input} />
                <DownloadButton content={input} filename="input.json" />
              </div>
            </div>
            <textarea
              value={input}
              onChange={handleInputChange}
              className="w-full h-96 font-mono text-sm p-4 border rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Paste your JSON here..."
            />
          </div>
        </div>

        <div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-medium text-gray-900">Output (PHP)</h2>
              <div className="flex space-x-2">
                <CopyButton text={output} />
                <DownloadButton content={output} filename="output.php" />
              </div>
            </div>
            {error ? (
              <div className="flex items-center space-x-2 text-red-600 p-4 bg-red-50 rounded-md">
                <AlertCircle className="h-5 w-5" />
                <span>{error}</span>
              </div>
            ) : (
              <SyntaxHighlighter
                language="php"
                style={githubGist}
                className="h-96 rounded-md"
                customStyle={{ height: '24rem' }}
              >
                {output}
              </SyntaxHighlighter>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PhpConverter;
